import { useState, useEffect } from 'react';

/**
 * 通用图片裁剪框 hook
 * @param {Object} options
 *   initCrop: 初始裁剪框 { x, y, w, h }
 *   minSize: 最小尺寸
 *   bound: { width, height } 区域限制
 * @returns {Object}
 */
export default function useCropBox({ initCrop, minSize = 40, bound = { width: 500, height: 400 } }) {
  const [crop, setCrop] = useState(initCrop);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(null); // 'nw' | 'ne' | 'sw' | 'se'
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // 当边界变化时，调整裁剪框位置
  useEffect(() => {
    setCrop(prevCrop => {
      const maxX = Math.max(0, bound.width - prevCrop.w);
      const maxY = Math.max(0, bound.height - prevCrop.h);
      return {
        x: Math.min(prevCrop.x, maxX),
        y: Math.min(prevCrop.y, maxY),
        w: Math.min(prevCrop.w, bound.width),
        h: Math.min(prevCrop.h, bound.height)
      };
    });
  }, [bound.width, bound.height]);

  // 拖动裁剪框
  const onMouseDown = e => {
    if (e.target.dataset.corner) {
      setResizing(e.target.dataset.corner);
      setOffset({ x: e.clientX, y: e.clientY });
    } else {
      setDragging(true);
      setOffset({ x: e.clientX - crop.x, y: e.clientY - crop.y });
    }
  };
  const onMouseMove = e => {
    if (dragging) {
      let nx = e.clientX - offset.x;
      let ny = e.clientY - offset.y;
      // 限制不超出边界
      nx = Math.max(0, Math.min(nx, bound.width - crop.w));
      ny = Math.max(0, Math.min(ny, bound.height - crop.h));
      setCrop(crop => ({ ...crop, x: nx, y: ny }));
    } else if (resizing) {
      let { x, y, w, h } = crop;
      let dx = e.clientX - offset.x;
      let dy = e.clientY - offset.y;
      if (resizing === 'se') {
        w = Math.max(minSize, Math.min(w + dx, bound.width - x));
        h = Math.max(minSize, Math.min(h + dy, bound.height - y));
      } else if (resizing === 'sw') {
        w = Math.max(minSize, Math.min(w - dx, x + w));
        h = Math.max(minSize, Math.min(h + dy, bound.height - y));
        x = Math.max(0, x + dx);
      } else if (resizing === 'ne') {
        w = Math.max(minSize, Math.min(w + dx, bound.width - x));
        h = Math.max(minSize, Math.min(h - dy, y + h));
        y = Math.max(0, y + dy);
      } else if (resizing === 'nw') {
        w = Math.max(minSize, Math.min(w - dx, x + w));
        h = Math.max(minSize, Math.min(h - dy, y + h));
        x = Math.max(0, x + dx);
        y = Math.max(0, y + dy);
      }
      setCrop({ x, y, w, h });
      setOffset({ x: e.clientX, y: e.clientY });
    }
  };
  const onMouseUp = () => {
    setDragging(false);
    setResizing(null);
  };

  return {
    crop,
    setCrop,
    dragging,
    resizing,
    offset,
    onMouseDown,
    onMouseMove,
    onMouseUp
  };
} 